import React, { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SellerModal({ open, onOpenChange }) {
  const { t } = useApp();
  const [parts, setParts] = useState([]);
  const [form, setForm] = useState({ pn: "", name: "", category: "drone", price: "", stock: "", condition: "New", location: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/seller/parts");
      setParts(data);
    } catch {}
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seller/parts", {
        ...form,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
      });
      toast.success(t("part_added"));
      setForm({ pn: "", name: "", category: "drone", price: "", stock: "", condition: "New", location: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    await api.delete(`/seller/parts/${id}`);
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-cyan-500/30 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seller-modal">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl text-slate-900">{t("sell_parts")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={add} className="grid grid-cols-2 gap-3 mt-2" data-testid="seller-part-form">
          <div>
            <Label className="text-slate-600 text-xs">{t("pn")}</Label>
            <Input data-testid="seller-pn" value={form.pn} onChange={(e) => setForm({ ...form, pn: e.target.value })} className="bg-slate-50 border-slate-300 mt-1" required />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">{t("part_name")}</Label>
            <Input data-testid="seller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-300 mt-1" required />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">{t("category")}</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="seller-category" className="bg-slate-50 border-slate-300 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="drone">{t("drone")}</SelectItem>
                <SelectItem value="aircraft">{t("aircraft")}</SelectItem>
                <SelectItem value="machinery">{t("machinery")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-600 text-xs">{t("condition")}</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger data-testid="seller-condition" className="bg-slate-50 border-slate-300 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Overhauled">Overhauled</SelectItem>
                <SelectItem value="Serviceable">Serviceable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-600 text-xs">{t("price")} (USD)</Label>
            <Input data-testid="seller-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-slate-50 border-slate-300 mt-1" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">{t("stock")}</Label>
            <Input data-testid="seller-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-slate-50 border-slate-300 mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-slate-600 text-xs">{t("location")}</Label>
            <Input data-testid="seller-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-slate-50 border-slate-300 mt-1" />
          </div>
          <Button data-testid="seller-add-btn" type="submit" className="col-span-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-head font-bold">
            <Plus size={16} className="mr-1" /> {t("add_part")}
          </Button>
        </form>

        <div className="mt-4">
          <h4 className="font-head text-sm text-slate-600 mb-2">{t("my_parts")} ({parts.length})</h4>
          <div className="space-y-2">
            {parts.map((p, i) => (
              <div key={p.id} className="ps-glow-card rounded-lg p-3 flex items-center justify-between" data-testid={`my-part-${i}`}>
                <div>
                  <span className="font-mono-ps text-cyan-700 text-xs mr-2">{p.pn}</span>
                  <span className="text-slate-900 text-sm">{p.name}</span>
                  <span className="text-slate-500 text-xs ml-2">${p.price} · {p.stock} pcs</span>
                </div>
                <button data-testid={`delete-part-${i}`} onClick={() => del(p.id)} className="text-red-400 hover:text-red-300"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
